/**
 * useBetaFeatureAccess
 *
 * Client hook for current-user beta feature visibility.
 *
 * Primary responsibilities:
 * - Fetch no-store beta access for client UI gates
 * - Provide a stable loading state for beta-controlled surfaces
 *
 * Dependencies:
 * - Beta feature access API
 */

'use client'

import { useQuery } from '@tanstack/react-query'

import type { BetaFeatureKey } from '@/lib/beta-features/registry'

const BETA_FEATURE_ACCESS_QUERY_KEY = ['beta-features', 'access'] as const

type BetaFeatureAccessMap = Partial<Record<BetaFeatureKey, boolean>>

// --------------

async function fetchBetaFeatureAccess(): Promise<BetaFeatureAccessMap> {
  const response = await fetch('/api/beta-features/access', {
    cache: 'no-store',
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error(await response.text())
  }

  const payload = (await response.json()) as {
    features?: BetaFeatureAccessMap
  }

  return payload.features ?? {}
}

// --------------

export function useBetaFeatureAccess() {
  const query = useQuery({
    gcTime: 5 * 60 * 1000,
    queryFn: fetchBetaFeatureAccess,
    queryKey: BETA_FEATURE_ACCESS_QUERY_KEY,
    retry: 1,
    staleTime: 30 * 1000
  })

  return {
    features: query.data ?? {},
    isError: query.isError,
    isLoading: query.isLoading
  }
}

// --------------

export function useBetaFeatureEnabled(featureKey: BetaFeatureKey) {
  const { features, isError, isLoading } = useBetaFeatureAccess()

  return {
    enabled: features[featureKey] === true,
    isError,
    isLoading
  }
}
